import type { Subscription, SubscriptionInterval } from './subscription';
import type { UserData } from './user';
import type { PlanLevel } from '~/lib/.client/plans';

export type OrganizationMembershipLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;

export const allOrganizationUserRoles = ['admin', 'billing_admin', 'member'];

export type OrganizationUserRoles = (typeof allOrganizationUserRoles)[number];

export type OrganizationProviderType = 'github' | 'custom';

export interface OrganizationSetupState {
  id: number;
  interval: SubscriptionInterval;
  level: PlanLevel;
}

export interface OrganizationSettings {
  allowInvitingNonmembers: boolean;
  maxVisibility: 'public' | 'private' | 'secret';
  memberProjectRole: 'viewer' | 'editor' | 'admin';
  projectStalenessDays: number | null;
  purgeStaleProjects: boolean;
  showIntegrations: boolean;
  showAccountSelector: boolean;
}

export interface CustomRegistry {
  id: string;
  url: string;
  scopes: string | null;
  token: string;
  updatedAt?: string;
}

export interface OrganizationData {
  id: number;
  slug: string;
  name: string;
  avatar?: string;
  role?: OrganizationUserRoles;
  upgradingRequests?: OrganizationMembership[];
  billing: {
    active?: boolean;
    paid?: boolean;
    subscription?: Subscription;
    tier: OrganizationMembershipLevel;
    nextTier?: OrganizationMembershipLevel;
    nextInterval?: SubscriptionInterval | null;
  } | null;
  owner: string;
  provider: OrganizationProviderType;
  canUsePaidFeatures: boolean;
  totalBoltTokenPurchases?: number;
  setupState?: OrganizationSetupState;
  organizationSettings?: OrganizationSettings;
  customRegistries?: CustomRegistry[];
  defaultCustomRegistry?: CustomRegistry;
}

export type OrganizationInvite = {
  token: string;
  acceptUrl: string;
  declineUrl: string;
  organization: OrganizationData;
};

export type OrganizationMembership = {
  id: number;
  role: OrganizationUserRoles;
  user: Required<Pick<UserData, 'avatar' | 'email' | 'username' | 'name' | 'cannotSendInvite'>> & {
    id: number;
    pending: boolean;
    lastActiveAt: string;
    providerId: string;
  };
  withMembership: boolean;
  downgraded: boolean;
};

export type ProjectIntegrations = {
  hosting?: {
    data: {
      hostingProvider: 'netlify' | 'bolt';
    };
  };
};
